import { useRef, useState } from "react";
import api, { imageUrl } from "@/lib/api";
import { toast } from "sonner";
import { Flag, ImagePlus, QrCode, X, Type, Link2, Loader2, Sparkles } from "lucide-react";

const PANEL = {
  background: "#0f0f16",
  backgroundImage:
    "repeating-linear-gradient(45deg, rgba(93,138,82,.055) 0px, rgba(93,138,82,.055) 10px, transparent 10px, transparent 20px)",
  border: "2px solid #4f7746",
};

const FONT_SIZES = [
  { key: "small", label: "Small" },
  { key: "medium", label: "Medium" },
  { key: "large", label: "Large" },
  { key: "xlarge", label: "Extra Large" },
  { key: "huge", label: "Huge (fills width)" },
];

const PREVIEW_FONT = { small: 20, medium: 27, large: 36, xlarge: 45, huge: 56 };

export default function MinuteEndcardEditor({ value, onChange, orientation = "portrait" }) {
  const ec = value;
  const logoRef = useRef(null);
  const qrRef = useRef(null);
  const [qrLink, setQrLink] = useState("");
  const [qrBusy, setQrBusy] = useState(false);
  const set = (upd) => onChange({ ...ec, ...upd });

  const generateQr = async () => {
    const url = qrLink.trim();
    if (!url) { toast.error("Type a link first"); return; }
    setQrBusy(true);
    try {
      const { data } = await api.post("/manuals/minute/qr", { url });
      set({ qr_image_id: data.image_id, qr_image: null });
      toast.success("QR code generated ✓");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't build that QR code");
    } finally { setQrBusy(false); }
  };

  const readImage = (e, key, idKey) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please choose an image (PNG or JPG)"); return; }
    if (f.size > 4 * 1024 * 1024) { toast.error("Please keep the image under 4 MB"); return; }
    const r = new FileReader();
    r.onload = () => set({ [key]: r.result, [idKey]: null });
    r.readAsDataURL(f);
  };

  const logoSrc = ec.logo_image || (ec.logo_image_id ? imageUrl(ec.logo_image_id) : null);
  const qrSrc = ec.qr_image || (ec.qr_image_id ? imageUrl(ec.qr_image_id) : null);
  const lines = [ec.line1, ec.line2, ec.line3].filter((l) => (l || "").trim());

  return (
    <div className="mt-5" data-testid="minute-endcard-step">
      <div className="flex items-center gap-1.5 font-extrabold text-sm text-white mb-2">
        <Flag size={15} className="text-[#8fbf82]" /> End card
        <span className="text-[10px] text-white/40 font-bold">(optional · plays for ~4s at the end)</span>
      </div>

      <div className="rounded-2xl p-3" style={PANEL}>
        {/* master toggle */}
        <label className="flex items-center gap-2.5 cursor-pointer select-none" data-testid="minute-endcard-enable-row">
          <input
            type="checkbox"
            data-testid="minute-endcard-enable"
            checked={!!ec.enabled}
            onChange={(e) => set({ enabled: e.target.checked })}
            className="w-5 h-5 accent-[#ffd21e] shrink-0"
          />
          <span className="text-sm font-extrabold text-white">Add a branded end card</span>
        </label>

        {ec.enabled && (
          <div className="mt-4 grid md:grid-cols-2 gap-4" data-testid="minute-endcard-body">
            {/* left — controls */}
            <div className="space-y-3">
              <div>
                <label className="block text-[11px] font-extrabold text-white/60 mb-1">Three lines of text</label>
                <div className="space-y-2">
                  {["line1", "line2", "line3"].map((k, idx) => (
                    <input
                      key={k}
                      data-testid={`minute-endcard-${k}`}
                      value={ec[k] || ""}
                      onChange={(e) => set({ [k]: e.target.value })}
                      placeholder={idx === 0 ? "e.g. Follow for more" : idx === 1 ? "e.g. @yourhandle" : "e.g. Link in bio"}
                      className="sms-input w-full px-3 py-2 text-sm"
                      maxLength={60}
                    />
                  ))}
                </div>
              </div>

              {/* font size */}
              <div>
                <label className="text-[11px] font-extrabold text-white/60 mb-1 flex items-center gap-1"><Type size={12} /> Text size</label>
                <select
                  data-testid="minute-endcard-fontsize"
                  value={ec.font_size || "medium"}
                  onChange={(e) => set({ font_size: e.target.value })}
                  className="sms-input w-full px-3 py-2 text-sm font-bold cursor-pointer"
                >
                  {FONT_SIZES.map((f) => (
                    <option key={f.key} value={f.key} data-testid={`minute-endcard-font-${f.key}`}>{f.label}</option>
                  ))}
                </select>
              </div>

              {/* colors */}
              <div className="flex gap-4">
                <div>
                  <label className="block text-[11px] font-extrabold text-white/60 mb-1">Text colour</label>
                  <input
                    type="color"
                    data-testid="minute-endcard-textcolor"
                    value={ec.text_color || "#ffffff"}
                    onChange={(e) => set({ text_color: e.target.value })}
                    className="w-12 h-9 rounded-lg bg-transparent border-2 border-[#3a4a34] cursor-pointer p-0.5"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-extrabold text-white/60 mb-1">Background</label>
                  <input
                    type="color"
                    data-testid="minute-endcard-bgcolor"
                    value={ec.bg_color || "#1a2818"}
                    onChange={(e) => set({ bg_color: e.target.value })}
                    className="w-12 h-9 rounded-lg bg-transparent border-2 border-[#3a4a34] cursor-pointer p-0.5"
                  />
                </div>
              </div>

              {/* domain */}
              <div>
                <label className="block text-[11px] font-extrabold text-white/60 mb-1">Your company domain</label>
                <input
                  data-testid="minute-endcard-domain"
                  value={ec.domain || ""}
                  onChange={(e) => set({ domain: e.target.value })}
                  placeholder="e.g. www.yourbrand.com"
                  className="sms-input w-full px-3 py-2 text-sm"
                  maxLength={80}
                />
              </div>

              {/* uploads */}
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="block text-[11px] font-extrabold text-white/60 mb-1">Logo</label>
                  <button
                    type="button"
                    data-testid="minute-endcard-logo-btn"
                    onClick={() => logoRef.current?.click()}
                    className="w-full h-20 rounded-xl border-2 border-dashed border-[#5d8a52] bg-black flex items-center justify-center overflow-hidden hover:border-[#a9d79b] transition-all relative"
                  >
                    {logoSrc ? (
                      <img src={logoSrc} alt="logo" className="w-full h-full object-contain p-1" />
                    ) : (
                      <span className="flex flex-col items-center text-[#a9d79b]"><ImagePlus size={18} /><span className="text-[10px] font-bold mt-0.5">Upload logo</span></span>
                    )}
                  </button>
                  {logoSrc && (
                    <button type="button" data-testid="minute-endcard-logo-remove" onClick={() => set({ logo_image: null, logo_image_id: null })} className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold text-white/50 hover:text-[#f7a0b4]"><X size={11} /> Remove logo</button>
                  )}
                  <input ref={logoRef} type="file" accept="image/*" onChange={(e) => readImage(e, "logo_image", "logo_image_id")} className="hidden" data-testid="minute-endcard-logo-input" />
                </div>
                <div className="flex-1">
                  <label className="block text-[11px] font-extrabold text-white/60 mb-1">QR code</label>
                  <button
                    type="button"
                    data-testid="minute-endcard-qr-btn"
                    onClick={() => qrRef.current?.click()}
                    className="w-full h-20 rounded-xl border-2 border-dashed border-[#5d8a52] bg-black flex items-center justify-center overflow-hidden hover:border-[#a9d79b] transition-all relative"
                  >
                    {qrSrc ? (
                      <img src={qrSrc} alt="qr" className="w-full h-full object-contain p-1" />
                    ) : (
                      <span className="flex flex-col items-center text-[#a9d79b]"><QrCode size={18} /><span className="text-[10px] font-bold mt-0.5">Upload QR</span></span>
                    )}
                  </button>
                  {qrSrc && (
                    <button type="button" data-testid="minute-endcard-qr-remove" onClick={() => set({ qr_image: null, qr_image_id: null })} className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold text-white/50 hover:text-[#f7a0b4]"><X size={11} /> Remove QR</button>
                  )}
                  <input ref={qrRef} type="file" accept="image/*" onChange={(e) => readImage(e, "qr_image", "qr_image_id")} className="hidden" data-testid="minute-endcard-qr-input" />
                </div>
              </div>
              {/* logo size slider — only relevant once a logo is set */}
              {logoSrc && (
                <div data-testid="minute-endcard-logoscale-row">
                  <label className="text-[11px] font-extrabold text-white/60 mb-1 flex items-center justify-between">
                    <span className="flex items-center gap-1"><ImagePlus size={12} /> Logo size</span>
                    <span className="text-[#a9d79b] tabular-nums">{Math.round((ec.logo_scale || 1) * 100)}%</span>
                  </label>
                  <input
                    type="range"
                    data-testid="minute-endcard-logoscale"
                    min="0.5" max="2.5" step="0.05"
                    value={ec.logo_scale || 1}
                    onChange={(e) => set({ logo_scale: parseFloat(e.target.value) })}
                    className="w-full accent-[#ffd21e] cursor-pointer"
                  />
                </div>
              )}

              {/* Auto-QR from a link */}
              <div>
                <label className="text-[11px] font-extrabold text-white/60 mb-1 flex items-center gap-1"><Link2 size={12} /> …or make a QR from a link</label>
                <div className="flex gap-2">
                  <input
                    data-testid="minute-endcard-qr-link"
                    value={qrLink}
                    onChange={(e) => setQrLink(e.target.value)}
                    placeholder="e.g. yourbrand.com/offer"
                    className="sms-input flex-1 px-3 py-2 text-xs"
                    maxLength={400}
                  />
                  <button
                    type="button"
                    data-testid="minute-endcard-qr-generate"
                    onClick={generateQr}
                    disabled={qrBusy}
                    className="shrink-0 inline-flex items-center gap-1 rounded-lg bg-[#4f7746] hover:bg-[#5d8a52] disabled:opacity-50 px-3 py-2 text-xs font-extrabold text-white transition-colors"
                  >
                    {qrBusy ? <Loader2 size={13} className="spin" /> : <Sparkles size={13} />} Generate
                  </button>
                </div>
              </div>
              <p className="text-[10px] text-white/40">Logo & QR look best as square PNGs, about <span className="text-[#a9d79b] font-bold">600 × 600 px</span> (transparent background welcome).</p>

              {/* branding toggle */}
              <label className="flex items-center gap-2.5 cursor-pointer select-none rounded-xl bg-black/40 px-3 py-2" data-testid="minute-endcard-branding-row">
                <input
                  type="checkbox"
                  data-testid="minute-endcard-branding"
                  checked={!!ec.show_branding}
                  onChange={(e) => set({ show_branding: e.target.checked })}
                  className="w-5 h-5 accent-[#ffd21e] shrink-0"
                />
                <span className="text-xs font-extrabold text-white">Show “Vittyyo” branding <span className="text-white/40 font-bold">(turn off to hide it)</span></span>
              </label>
            </div>

            {/* right — live preview */}
            <div>
              <label className="block text-[11px] font-extrabold text-white/60 mb-1">Preview</label>
              <div
                data-testid="minute-endcard-preview"
                className="mx-auto rounded-2xl overflow-hidden border-2 border-[#3a4a34] flex flex-col items-center justify-center text-center px-4"
                style={{ background: ec.bg_color || "#1a2818", color: ec.text_color || "#ffffff", aspectRatio: orientation === "landscape" ? "16/9" : "9/16", maxWidth: orientation === "landscape" ? 480 : 320, width: "100%", position: "relative" }}
              >
                {logoSrc && <img src={logoSrc} alt="logo preview" className="object-contain mb-3" style={{ maxHeight: 84 * (ec.logo_scale || 1), maxWidth: `${Math.min(92, 55 * (ec.logo_scale || 1))}%` }} />}
                {lines.map((l, i) => (
                  <div key={i} className="font-extrabold leading-tight break-words" style={{ fontSize: PREVIEW_FONT[ec.font_size || "medium"] }}>{l}</div>
                ))}
                {(ec.domain || "").trim() && <div className="mt-2 text-sm font-bold opacity-90 break-all">{ec.domain}</div>}
                {qrSrc && <img src={qrSrc} alt="qr preview" className="mt-3 w-20 h-20 object-contain bg-white rounded-md p-0.5" />}
                {ec.show_branding && (
                  <div className="absolute bottom-2 inset-x-0 text-[9px] font-extrabold opacity-80">Step<span style={{ color: "#a9c39c" }}>My</span>Step</div>
                )}
                {!lines.length && !logoSrc && !qrSrc && !(ec.domain || "").trim() && (
                  <div className="text-[11px] opacity-50">Your end card preview</div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
